import { Trash2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  useDeleteMedicalProviderMutation,
  useGetMedicalProvidersQuery,
} from '@/services/api/treatmentService';
import type {
  IngestionSection,
  IngestionSections,
  IngestionSectionStatus,
} from '@/services/types/case-ingestion-types';
import { ContactHandleType } from '@/services/types/treatment-types';

import { useResetIngestionSections, useStepSave } from '../../hooks';
import { IngestionNotFoundBanner , IngestionStepLayout } from '../../layout';
import { IngestionStepSkeleton } from '../../skeletons';
import { ProviderSearchInline } from './ProviderSearchInline';

interface TreatmentStepProps {
  matterId: string;
  sectionStatuses: IngestionSections;
  onSaveComplete?: (success: boolean) => void;
}

export const TreatmentStep = ({
  matterId,
  sectionStatuses,
  onSaveComplete,
}: TreatmentStepProps) => {
  const { saveStep } = useStepSave();
  const { resetSections } = useResetIngestionSections();
  // Initialize notFoundChecked from audit record (sectionStatuses guaranteed loaded via parent gate)
  const [notFoundChecked, setNotFoundChecked] = useState(
    () => sectionStatuses.medical_providers === 'not_found'
  );
  const hasHydrated = useRef(false);

  // Fetch existing providers
  // Note: Cross-slice mutations (from providerDirectoryV2Service) don't auto-invalidate this cache,
  // so we pass refetch to ProviderSearchInline as onProviderAdded callback
  const { data: providers = [], isLoading: isLoadingProviders, refetch } = useGetMedicalProvidersQuery(matterId, {
    skip: !matterId,
  });

  const [deleteMedicalProvider, { isLoading: isDeleting }] =
    useDeleteMedicalProviderMutation();

  // Mark as hydrated after initial load completes
  useEffect(() => {
    if (isLoadingProviders) return;
    hasHydrated.current = true;
  }, [isLoadingProviders]);

  const handleDeleteProvider = async (providerId: string) => {
    const isLastProvider = providers.length === 1;

    try {
      await deleteMedicalProvider({ matterId, providerId }).unwrap();
      if (isLastProvider) {
        await resetSections(['medical_providers']);
      }
    } catch {
      toast.error('Failed to remove provider');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only update section status if user made an explicit choice:
    // - Checked "not found" checkbox → 'not_found'
    // - Added providers → 'found'
    // - Neither → don't update (stays null/unreviewed)
    const sectionUpdates: Partial<Record<IngestionSection, IngestionSectionStatus>> = {};
    if (notFoundChecked) {
      sectionUpdates.medical_providers = 'not_found';
    } else if (providers.length > 0) {
      sectionUpdates.medical_providers = 'found';
    }

    // Providers are already saved via immediate API calls in ProviderSearchInline
    // Just mark the step as complete if user made a choice
    const success = await saveStep(
      async () => {
        // No additional API calls needed - providers already created
      },
      sectionUpdates,
      {
        successMessage:
          providers.length > 0
            ? `${providers.length} provider(s) saved`
            : 'Step completed',
        errorMessage: 'Failed to save step',
      },
    );
    onSaveComplete?.(success);
  };

  // Show skeleton during initial load
  if (isLoadingProviders && !hasHydrated.current) {
    return <IngestionStepSkeleton />;
  }

  return (
    <IngestionStepLayout
      title="Medical Treatment"
      description="Add medical providers where the client received treatment"
    >
      {/* Not found checkbox - hidden when providers exist */}
      {providers.length === 0 && (
        <div className="flex justify-end">
          <IngestionNotFoundBanner
            id="treatment-not-found"
            checked={notFoundChecked}
            onCheckedChange={setNotFoundChecked}
            label="No providers found"
          />
        </div>
      )}

      <form
        id="ingestion-step-form"
        onSubmit={onSubmit}
        className="space-y-2"
      >
        {!notFoundChecked && (
          <>
            {/* Provider Search - calls API immediately on add */}
            <ProviderSearchInline matterId={matterId} onProviderAdded={refetch} />

            {/* Added Providers List - displays from query results */}
            {providers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-zinc-700">
                  Added providers ({providers.length})
                </Label>
                {providers.map((provider) => {
                  const phone = provider.handles?.find(
                    (h) => h.handle_type === ContactHandleType.PHONE,
                  )?.value;
                  const website = provider.handles?.find(
                    (h) => h.handle_type === ContactHandleType.WEBSITE,
                  )?.value;

                  return (
                    <Card key={provider.id} className="bg-zinc-50">
                      <CardContent className="p-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-zinc-900 text-xs truncate">
                              {provider.name || 'Unnamed provider'}
                            </div>
                            {provider.address && (
                              <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                                {provider.address}
                              </div>
                            )}
                            {(phone || website) && (
                              <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                                {[phone, website].filter(Boolean).join(' • ')}
                              </div>
                            )}
                            {provider.provider_service_ref_id && (
                              <span className="inline-flex items-center text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                                From directory
                              </span>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProvider(provider.id)}
                            disabled={isDeleting}
                            className="h-6 px-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

      </form>
    </IngestionStepLayout>
  );
};
